import { DOCUMENT } from '@angular/common'
import { Inject, Injectable } from '@angular/core'

import { COLOR_THEME_SUPPORTED_DOMAINS } from '../constants/theme.constant'
import { DomainService } from './domain.service'

@Injectable({
	providedIn: 'root',
})
export class ThemeColorService {
	private readonly storageKey = 'app-theme-color'

	readonly supportsColorTheme: boolean

	readonly colorOptions = [
		{ key: 'navy-blue', label: 'Navy Blue', color: '#020d31' },
		{ key: 'modern-olive', label: 'Modern Olive', color: '#1c280d' },
		{ key: 'dark-gray', label: 'Dark Gray', color: '#12191a' },
		{ key: 'warm-ember', label: 'Warm Ember', color: '#230501' },
	]

	readonly defaultColor = 'navy-blue'

	constructor(
		@Inject(DOCUMENT) private readonly document: Document,
		private readonly domainService: DomainService,
	) {
		this.supportsColorTheme = COLOR_THEME_SUPPORTED_DOMAINS.includes(this.domainService.domain as any)
		if (this.supportsColorTheme) {
			this.applyThemeColor(this.getThemeColor())
		}
	}

	getThemeColor(): string {
		const color = localStorage.getItem(this.storageKey)
		return this.colorOptions.some((opt) => opt.key === color) ? color! : this.defaultColor
	}

	setThemeColor(colorKey: string): void {
		const option = this.colorOptions.find((opt) => opt.key === colorKey)
		if (!option) {
			console.warn(`Invalid theme color: "${colorKey}". Falling back to default.`)
			colorKey = this.defaultColor
		}

		localStorage.setItem(this.storageKey, colorKey)
		this.applyThemeColor(colorKey)
	}

	private applyThemeColor(colorKey: string): void {
		const option = this.colorOptions.find((opt) => opt.key === colorKey)
		if (!option) return

		const existingStyle = this.document.getElementById('custom-theme-color')
		if (existingStyle) {
			existingStyle.remove()
		}

		const rgb = this.hexToRgb(option.color)
		const shade = this.adjustColor(option.color, -10)
		const tint = this.adjustColor(option.color, 10)

		const style = this.document.createElement('style')
		style.id = 'custom-theme-color'
		style.textContent = `
			* {
				--ion-color-secondary: ${option.color} !important;
				--ion-color-secondary-rgb: ${rgb} !important;
				--ion-color-secondary-contrast: #ffffff !important;
				--ion-color-secondary-contrast-rgb: 255, 255, 255 !important;
				--ion-color-secondary-shade: ${shade} !important;
				--ion-color-secondary-tint: ${tint} !important;

				--ion-color-header: ${option.color} !important;
				--ion-color-header-rgb: ${rgb} !important;
				--ion-color-header-contrast: #ffffff !important;
				--ion-color-header-contrast-rgb: 255, 255, 255 !important;
				--ion-color-header-shade: ${shade} !important;
				--ion-color-header-tint: ${tint} !important;

				--ion-color-drawer: ${option.color} !important;
				--ion-color-drawer-rgb: ${rgb} !important;
				--ion-color-drawer-contrast: #ffffff !important;
				--ion-color-drawer-contrast-rgb: 255, 255, 255 !important;
				--ion-color-drawer-shade: ${shade} !important;
				--ion-color-drawer-tint: ${tint} !important;
			}
		`
		this.document.head.appendChild(style)
	}

	private hexToRgb(hex: string): string {
		const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
		return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '0, 0, 0'
	}

	private adjustColor(hex: string, percent: number): string {
		const num = parseInt(hex.replace('#', ''), 16)
		const amt = Math.round(2.55 * percent)
		const R = (num >> 16) + amt
		const G = ((num >> 8) & 0x00ff) + amt
		const B = (num & 0x0000ff) + amt
		return (
			'#' +
			(0x1000000 + (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 + (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 + (B < 255 ? (B < 1 ? 0 : B) : 255))
				.toString(16)
				.slice(1)
		)
	}
}
